/* Homepage scroll-in reveals, modelled on flocksafety.com.

   Two treatments, both fired once by an IntersectionObserver:

   1. Section titles are split into words. Each word starts as a ghost
      blue-grey, slightly dropped and transparent, and settles into place on
      a long ease-out, one word 58ms after the last.
   2. Everything else ("items": cards, paragraphs, buttons, marquees) fades
      up from 2rem below over 500ms, with a 100ms stagger between siblings.

   All the motion lives in CSS (.reveal-title / .reveal-up in main.scss);
   this file only decides which elements take part, applies the initial
   classes, and flips .is-visible when they scroll into view. Because the
   hidden state is only ever applied here, pages without JS (and every page
   other than the homepage) render untouched.

   Required from main.js and run on every page, so it bails immediately
   unless the homepage container (#index) is present. */
(function () {
  var root = document.getElementById('index');
  if (!root) return;

  /* index.ejs pre-hides the above-the-fold targets (#index.reveal-pending)
     so they can't flash before this runs. Whatever happens below, that
     class must come off — including when we bail out. */
  function releasePending() {
    root.classList.remove('reveal-pending');
  }

  /* No reveals on mobile (matches the $mobile breakpoint in scss): small
     screens get everything visible from the first paint. */
  var MOBILE_QUERY = '(max-width: 800px)';

  if (
    !('IntersectionObserver' in window) ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    window.matchMedia(MOBILE_QUERY).matches
  ) {
    releasePending();
    return;
  }

  var STEP = 100; // ms between siblings …
  var MAX_STAGGER = 4; // … capped at 0..400ms, like the reference's scroll-in-1..4
  /* Fire slightly before the element's top edge enters the viewport (5% of
     its height early). The reference waits until the element is 8% *in*,
     which felt late here. */
  var ROOT_MARGIN = '0px 0px 5% 0px';

  /* ------------------------------------------------------------------ */
  /* What gets revealed. `title` = word reveal, `items` = fade-up, with   */
  /* the stagger index counted within `group`. `step`/`cap` override the  */
  /* default stagger; `together` observes the group container instead of  */
  /* each item, so the whole set plays as one cascade when the container  */
  /* enters view (for marquees, whose items scroll in and out of view).   */
  /* Selectors are scoped to #index.                                      */
  var CONFIG = [
    /* Hero (plays on load — everything here is in view immediately). All
       blocks, headline included, so the hero reads as one consistent
       cascade; the word-by-word reveal is reserved for section titles. */
    {
      group: '#hero .left',
      items: '.rating, .hero-headline, .hero-subhead, .check-bullets, .hero-trustline, .actions'
    },
    { items: '#hero .right .img' },

    /* Featured brands: title as a block, then the three marquee rows fade
       up one after the other when the strip enters view. */
    { items: '#featured-brands h4' },
    {
      group: '#featured-brands .brands-marquee',
      items: ':scope > .marquee',
      together: true,
      step: 120
    },

    /* Go live in 5 minutes */
    { title: '#launch-in-minutes h3' },
    { group: '#launch-in-minutes .steps', items: '.step' },
    { items: '#launch-in-minutes .actions' },

    /* Key features (video blocks) */
    { title: '#key-features > .wrapper > h4, #key-features .content > h6' },
    { group: '#key-features .features-container', items: '.feature-wrapper' },

    /* Synthetic research */
    { title: '#synthetic-research-block > .wrapper > h3, #synthetic-research-block > .wrapper > h6' },
    { group: '#synthetic-research-block .features-grid', items: '.feature' },
    { items: '#synthetic-research-block .actions' },

    /* Ready to get started */
    { title: '#get-started-homepage h3, #get-started-homepage h6' },
    { items: '#get-started-homepage .actions' },

    /* Latest case studies */
    { title: '#from-the-blog .title h3' },
    { items: '#from-the-blog .title p' },
    { group: '#from-the-blog .list', items: '.post-thumbnail' },
    { group: '#from-the-blog .below', items: ':scope > div' },

    /* Stars / reviews / integrations */
    { items: '#stars-banner' },
    { title: '#review-marquee > h6, .integrations-block > h6' },
    { items: '#review-marquee > div, #review-marquee .read-more, .integrations-block .marquee, .integrations-block .read-more' },

    /* MCP block */
    { items: '.mcp-server-block .mcp-prompt-marquee' },
    { title: '.mcp-inner h3' },
    { items: '.mcp-inner .mcp-subtitle' },
    { group: '.mcp-inner .mcp-features', items: '.mcp-feature' },
    { items: '.mcp-inner .mcp-install, .mcp-inner > a' },

    /* Pricing */
    { title: '#pricing-plans > .wrapper > h3, #pricing-plans > .wrapper > h6' },
    { group: '#pricing-plans .plans', items: '.plan' },
    { items: '#pricing-plans .below' },

    /* Work smarter (use cases) */
    { title: '#how-to-zigpoll .title h3' },
    { items: '#how-to-zigpoll .title p' },
    { group: '#how-to-zigpoll .list', items: '.item' },
    { items: '#how-to-zigpoll .below' },

    /* FAQ */
    { title: '#faq h3' },
    { group: '#faq .questions ul', items: 'li' },

    /* Create account / questions */
    { title: '#create-account > .wrapper > h3, #create-account > .wrapper > h6, #questions-feedback h3, #questions-feedback h6' },
    { items: '#create-account > .wrapper > .actions, #questions-feedback .actions' }
  ];

  /* ------------------------------------------------------------------ */
  /* Word splitting (ported from the reference): wrap each run of         */
  /* non-whitespace text in a span, recursing into child elements so     */
  /* nested <span>/<strong> styling survives.                            */
  function splitTextNode(textNode) {
    var frag = document.createDocumentFragment();
    textNode.textContent.split(/(\s+)/).forEach(function (part) {
      if (!part) return;
      if (/^\s+$/.test(part)) {
        frag.appendChild(document.createTextNode(part));
        return;
      }
      /* A custom tag rather than <span>: several section headings style
         their own <span> highlights with id-level selectors, which would
         otherwise catch every word wrapper too. Unknown elements render
         inline and carry no styling of their own. */
      var word = document.createElement('rw-word');
      word.textContent = part;
      frag.appendChild(word);
    });
    textNode.parentNode.replaceChild(frag, textNode);
  }

  function splitElement(el) {
    Array.prototype.slice.call(el.childNodes).forEach(function (node) {
      if (node.nodeType === 3) {
        splitTextNode(node);
      } else if (node.nodeType === 1 && node.tagName !== 'RW-WORD') {
        splitElement(node);
      }
    });
  }

  function setupTitle(el) {
    if (el.classList.contains('reveal-title')) return;
    splitElement(el);
    var words = el.querySelectorAll('rw-word');
    for (var i = 0; i < words.length; i++) {
      words[i].style.setProperty('--word-index', i);
    }
    el.classList.add('reveal-title');
    /* Once the last word has landed, drop the reveal classes so the words
       are left as plain inline-blocks with no lingering filter/transition. */
    el._revealDone = (words.length - 1) * 58 + 64 + 1020 + 100;
  }

  function setupItem(el, index, step, cap) {
    if (el.classList.contains('reveal-up')) return;
    var delay = Math.min(index, cap) * step;
    el.style.setProperty('--reveal-delay', delay + 'ms');
    el.classList.add('reveal-up');
    /* After the fade, strip the reveal classes so the element's own
       transitions/hover transforms take over again. */
    el._revealDone = delay + 500 + 100;
  }

  /* ------------------------------------------------------------------ */
  var pending = [];

  CONFIG.forEach(function (entry) {
    if (entry.title) {
      var titles = root.querySelectorAll(entry.title);
      for (var t = 0; t < titles.length; t++) {
        setupTitle(titles[t]);
        pending.push(titles[t]);
      }
    }
    if (entry.items) {
      var step = entry.step || STEP;
      var cap = entry.cap || MAX_STAGGER;
      var containers = entry.group ? root.querySelectorAll(entry.group) : [root];
      for (var c = 0; c < containers.length; c++) {
        var container = containers[c];
        var items = container.querySelectorAll(entry.items);
        var set = [];
        for (var i = 0; i < items.length; i++) {
          setupItem(items[i], entry.group ? i : 0, step, cap);
          set.push(items[i]);
        }
        if (entry.together) {
          container._revealItems = set;
          pending.push(container);
        } else {
          pending.push.apply(pending, set);
        }
      }
    }
  });

  /* The reveal classes are on; hand over from the pre-hide in the same
     frame, then force a style flush so the hidden state is committed before
     any .is-visible lands — otherwise in-view elements would skip the
     transition and simply appear. */
  releasePending();
  if (!pending.length) return;
  void root.offsetWidth;

  function reveal(el) {
    if (el._revealItems) {
      el._revealItems.forEach(reveal);
      el._revealItems = null;
      return;
    }
    if (el.classList.contains('is-visible')) return;
    el.classList.add('is-visible');
    setTimeout(function () {
      el.classList.remove('reveal-up', 'reveal-title', 'is-visible');
      el.style.removeProperty('--reveal-delay');
    }, el._revealDone || 1200);
  }

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        observer.unobserve(entry.target);
        remaining.splice(remaining.indexOf(entry.target), 1);
      });
    },
    { threshold: 0, rootMargin: ROOT_MARGIN }
  );

  var remaining = [];
  pending.forEach(function (el) {
    /* Anything already scrolled past (e.g. reload with restored scroll
       position) is shown straight away rather than left hidden. */
    if (el.getBoundingClientRect().bottom < 0) {
      reveal(el);
      return;
    }
    remaining.push(el);
    observer.observe(el);
  });

  /* IntersectionObserver only reports *changes* in intersection, so an
     element that jumps from below the viewport to above it in one big
     scroll (fast wheel, keyboard End, anchor link) would never fire.
     Catch those on scroll and show them. */
  var ticking = false;
  window.addEventListener(
    'scroll',
    function () {
      if (ticking || !remaining.length) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        for (var i = remaining.length - 1; i >= 0; i--) {
          var el = remaining[i];
          if (el.getBoundingClientRect().bottom < 0) {
            reveal(el);
            observer.unobserve(el);
            remaining.splice(i, 1);
          }
        }
      });
    },
    { passive: true }
  );
})();
